// src/createDescendantsIndexContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";

// src/useLayoutEffect.ts
import { useEffect, useLayoutEffect as useLayoutEffectDefault } from "react";
var useLayoutEffect = typeof window !== "undefined" ? useLayoutEffectDefault : useEffect;

// src/createDescendantsIndexContext.tsx
var createDescendantsIndexContext = () => {
  const DescendantsIndexContext = createContext({});
  const DescendantsIndexProvider = ({ children }) => {
    const [nodes, setNodes] = useState(/* @__PURE__ */ new Map());
    const setRef = useCallback((ref, index = void 0) => {
      if (!(ref == null ? void 0 : ref.current))
        return;
      setNodes((nodes2) => {
        const elem = ref == null ? void 0 : ref.current;
        if (!elem)
          return nodes2;
        const newNodes = new Map(nodes2);
        newNodes.set(elem, index);
        return newNodes;
      });
    }, []);
    const removeRef = useCallback((ref) => {
      if (!(ref == null ? void 0 : ref.current))
        return;
      setNodes((nodes2) => {
        const elem = ref == null ? void 0 : ref.current;
        if (!elem)
          return nodes2;
        const newNodes = new Map(nodes2);
        newNodes.delete(elem);
        return newNodes;
      });
    }, []);
    useLayoutEffect(() => {
      if (nodes.size < 2)
        return;
      const newNodes = new Map(nodes);
      const nodesArray = Array.from(newNodes.keys());
      const sortedNodesArray = sortNodes(nodesArray);
      sortedNodesArray.forEach((node, i) => {
        newNodes.set(node, i);
      });
      if (!isEqual(newNodes, nodes)) {
        setNodes(newNodes);
      }
    }, [nodes]);
    const getIndex = useCallback((ref) => {
      return ref.current ? nodes.get(ref.current) : void 0;
    }, [nodes]);
    const getByIndex = useCallback((index) => {
      const entry = [...nodes.entries()].find(([, elementIndex]) => {
        return elementIndex === index;
      });
      if (!entry)
        return null;
      return { ref: { current: entry[0] }, index: entry[1] };
    }, [nodes]);
    const total = nodes.size;
    const value = useMemo(() => {
      return {
        setRef,
        removeRef,
        getIndex,
        total,
        getByIndex
      };
    }, [setRef, getIndex, removeRef, total, getByIndex]);
    return /* @__PURE__ */ React.createElement(DescendantsIndexContext.Provider, {
      value
    }, children);
  };
  const useDescendantIndex = () => {
    const ref = useRef(null);
    const { setRef, getIndex, removeRef, total, getByIndex } = useContext(DescendantsIndexContext);
    useLayoutEffect(() => {
      setRef == null ? void 0 : setRef(ref);
      return () => {
        removeRef == null ? void 0 : removeRef(ref);
      };
    }, [setRef, removeRef]);
    return { ref, index: getIndex == null ? void 0 : getIndex(ref), total, getByIndex };
  };
  return {
    DescendantsIndexProvider,
    useDescendantIndex,
    DescendantsIndexContext
  };
};
function sortNodes(nodes) {
  return nodes.sort((a, b) => {
    const compareMask = a.compareDocumentPosition(b);
    if (compareMask & Node.DOCUMENT_POSITION_FOLLOWING || compareMask & Node.DOCUMENT_POSITION_CONTAINED_BY) {
      return -1;
    }
    if (compareMask & Node.DOCUMENT_POSITION_PRECEDING || compareMask & Node.DOCUMENT_POSITION_CONTAINS) {
      return 1;
    }
    return 0;
  });
}
var isEqual = (map1, map2) => {
  if (map1.size !== map2.size)
    return false;
  for (const [key, value] of map1.entries()) {
    if (value !== map2.get(key))
      return false;
  }
  return true;
};
export {
  createDescendantsIndexContext
};
